import type {
  ToolConfigOnSubmitPayloads,
  ToolConfigProps,
} from '@timelyapp/integration-cloud-sdk/components/data/tool/tool.config.types';
import React, { lazy, Suspense, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ticApi } from 'redux/modules/tic/api-slice/tic.apiSlice';
import { getContextualTicAccounts } from 'redux/modules/tic/tic.auth.selectors';
import { setMemoryConfig } from 'redux/modules/tic/tic.memories.actions';
import { getMemoryConstructionParams } from 'redux/modules/tic/tic.memories.selectors';
import type { ThunkDispatch } from 'types/redux';

const ToolConfigModal = lazy(
  () => import('@timelyapp/integration-cloud-sdk/components/data/tool/ToolConfigModal')
);

type Props = Omit<ToolConfigProps, 'onChange' | 'accountId'>;

const TicToolConfigModal: React.FC<Props> = props => {
  const { onClose } = props;
  const constructionParams = useSelector(getMemoryConstructionParams);
  const { personalSpace } = useSelector(getContextualTicAccounts);
  const dispatch = useDispatch<ThunkDispatch>();

  /**
   * ATM, all "save to server" actions are handled by the parent component,
   * so we're mostly just mirroring state back to Redux.
   *
   * For toolConfig` set RTK Query cache direct.
   */
  const handleToolConfigSubmit = useCallback(
    (change: ToolConfigOnSubmitPayloads) => {
      switch (change.type) {
        case 'toolConfig': {
          const { payload } = change;
          dispatch(
            ticApi.util.updateQueryData('getToolConfigs', undefined, draftConfigs => {
              let upserted = false;
              draftConfigs.configs = draftConfigs.configs.map(config => {
                // 1 config per tool, so we can assume tool_id/tool_raw is unique in leu of `id`
                const existingIdentifier = config.tool_id || config.tool_raw;
                const incomingIdenfier = payload.tool_id || payload.tool_raw;

                if (existingIdentifier !== incomingIdenfier) return config;

                upserted = true;
                return payload;
              });

              if (!upserted) {
                draftConfigs.configs.push(payload);
              }
            })
          );
          break;
        }
        case 'memoryQueries': {
          dispatch(setMemoryConfig(change.payload));
          break;
        }
        default:
          break;
      }
      onClose?.();
    },
    [dispatch, onClose]
  );

  return (
    <Suspense>
      <ToolConfigModal
        memoryQueries={constructionParams}
        accountId={personalSpace?.id}
        {...props}
        onSubmit={handleToolConfigSubmit}
        // onChange={(change: ToolConfigOnSubmitPayloads) => console.log('onChange for optimistic updates', change)}
      />
    </Suspense>
  );
};

export default TicToolConfigModal;
